/* eslint-disable complexity */
import React, { useContext, useEffect, useState } from 'react';
import {
	ILabel,
	ITollSectionOffer,
	ITypeCartFields,
	IVignetteOffer,
	OfferContext,
	VignetteContext,
	calculateCurrentStep,
	formatVignetteDatesRange,
	getLabelValue,
} from '@adac-mp/shared';
import { CircularProgressBar, ProductSummary, South, renderProductIcon, ProductProcessRow } from '../../index';
import { useViewport } from '../../hooks/useViewport';

interface IProductConfigurationProps {
	reuse?: boolean;
	currStep: number;
	totalSteps: number;
	scrollToStep: (step: number) => void;
}

type ProductProcessRowType = {
	label: string;
	value: string | JSX.Element;
};

const renderSelectedTollRoutes = (selectedVignetteTollSections: ITollSectionOffer[]) => (
	<span className="flex gap-1">
		{selectedVignetteTollSections?.map(({ serviceCatalogName, routeDirection }, index) => (
			<span key={index} className="inline-flex">
				{routeDirection && <South />}
				{`${serviceCatalogName.slice(0, 3).trim()}${
					index !== selectedVignetteTollSections.length - 1 ? ',' : ''
				}`}
			</span>
		))}
	</span>
);

const getArticleValue = (moduleTypeId: number, currSelectedProduct: IVignetteOffer, labels: ILabel[]) => {
	switch (moduleTypeId) {
		case 2:
			return currSelectedProduct?.serviceCatalogName;
		case 3:
			return `${getLabelValue('mp.digital', labels)} ${currSelectedProduct?.moduleType}`;
		case 4:
			return `${getLabelValue('mp.tollSection', labels)} ${currSelectedProduct?.moduleType?.toUpperCase()}`;
		default:
			return '';
	}
};

const getProductProcessRows = (
	currSelectedProduct: IVignetteOffer,
	isVignette: boolean,
	currStep: number,
	selectedVignetteTollSections: ITollSectionOffer[] | undefined,
	reuse: boolean | undefined,
	labels: ILabel[],
	contentfulLabels?,
) => {
	const cartLabels = contentfulLabels?.cart as ITypeCartFields;
	const productProcessRows: ProductProcessRowType[] = [
		{
			label: getLabelValue('mp.article', labels) || 'Artikel',
			value: getArticleValue(currSelectedProduct.moduleTypeId, currSelectedProduct, labels),
		},
	];

	if (currSelectedProduct?.moduleTypeId !== 4) {
		productProcessRows.push(
			isVignette
				? {
						label:
							currStep > 2 && currSelectedProduct.dates
								? getLabelValue('mp.valid', labels) || 'Gültig'
								: cartLabels?.setValidityPeriod || 'Gültigkeitsdauer festlegen',
						value:
							currSelectedProduct?.dates && currStep > 2
								? `${formatVignetteDatesRange(
										currSelectedProduct.dates.end,
										currSelectedProduct.dates.start,
								  )}`
								: '',
				  }
				: {
						label:
							currStep > 2 && selectedVignetteTollSections && selectedVignetteTollSections.length > 0
								? cartLabels?.route || 'Strecke'
								: cartLabels?.chooseRoute || 'Strecken auswählen',
						value:
							selectedVignetteTollSections && selectedVignetteTollSections.length > 0 && currStep > 2
								? renderSelectedTollRoutes(selectedVignetteTollSections)
								: '',
				  },
		);
	}

	if (!reuse) {
		productProcessRows.push({ label: cartLabels?.addLicensePlate || 'Kennzeichen eingeben', value: '' });
	}

	return productProcessRows;
};

export const ProductConfiguration = ({ reuse, currStep, totalSteps, scrollToStep }: IProductConfigurationProps) => {
	const { selectedProducts, productsTypeMap, selectedSectionTolls, contentfulLabels } = useContext(VignetteContext);
	const currSelectedProduct = selectedProducts[0]; //only for sigle country flows for now
	const { labels } = useContext(OfferContext);
	const { viewport } = useViewport();
	const [activeStep, setActiveStep] = useState(currStep);
	const isVignette = currSelectedProduct.moduleTypeId === 2;
	const isFlex = currSelectedProduct.moduleTypeId === 4;

	useEffect(() => {
		setActiveStep(currStep);
	}, [currStep]);

	const configurationIcon = () => (
		<span className="relative">
			<span className="bg-black/[0.56] rounded w-full h-full flex items-center justify-center absolute">
				<CircularProgressBar
					withBackgroundRing
					className="rotate-[60deg]"
					size={36}
					step={
						calculateCurrentStep(productsTypeMap, selectedSectionTolls, currStep, totalSteps) / totalSteps
					}
				/>
			</span>
			{renderProductIcon({
				moduleTypeId: currSelectedProduct.moduleTypeId,
				supplierId: currSelectedProduct.supplierId,
				tollClass: currSelectedProduct.tollClass,
				size: viewport === 'desktop' ? '72' : '36',
			})}
		</span>
	);

	return (
		<div className="px-1.5 pt-5">
			<ProductSummary
				simple
				className="rounded pb-4 bg-white"
				cartLabels={contentfulLabels?.cart as ITypeCartFields}
				style={{ borderStyle: 'dashed', borderColor: '#D6D6D6' }}
				showProductPrice={isVignette}
				offer={currSelectedProduct}
				title={
					isVignette || isFlex
						? currSelectedProduct.serviceCatalogName
						: `${getLabelValue('mp.digital', labels) || 'Digitale'} ${currSelectedProduct?.moduleType}`
				}
				icon={configurationIcon()}
				category={
					isVignette
						? {
								moduleDetail: currSelectedProduct.moduleDetail,
								moduleDetailId: currSelectedProduct.moduleDetailId,
						  }
						: undefined
				}>
				<div className='relative after:content-[""] after:absolute after:w-px after:h-4/5 after:top-3 after:bottom-3 after:bg-gray-600 after:left-[39.5px] md:after:left-[59.5px]'>
					{getProductProcessRows(
						currSelectedProduct,
						isVignette,
						currStep,
						selectedSectionTolls,
						reuse,
						labels,
						contentfulLabels,
					).map(({ label, value }, index) => (
						<ProductProcessRow
							key={index}
							label={label}
							value={value}
							index={index}
							currStep={currStep}
							supplier={currSelectedProduct.supplierId}
							activeStep={activeStep}
							setActiveStep={setActiveStep}
							scrollToStep={scrollToStep}
						/>
					))}
				</div>
			</ProductSummary>
		</div>
	);
};
